import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";

import SEO from "../components/SEO";
import { ArrowLeft } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useToast } from "../hooks/use-toast";

const SepticCalculatorPage = () => {
  const { toast } = useToast();
  
  // Form state
  const [sqft, setSqft] = useState("1500-2000");
  const [bedrooms, setBedrooms] = useState(3);
  const [bathrooms, setBathrooms] = useState(2);
  const [soil, setSoil] = useState('medium');
  
  // Contact form state
  const [contactForm, setContactForm] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    city: ''
  });

  // Calculations
  const [calculations, setCalculations] = useState({
    costTrad: 0,
    costEljen: 0
  });

  // Calculate everything when inputs change
  useEffect(() => {
    // === BASELINE COSTS (ALWAYS INCLUDED) ===
    const tradBaseline = 6000 + 2500;  // Tank + Permitting = $8,500
    const eljenBaseline = 6000 + 4500 + 500; // Tank + Permitting + First Year Service = $11,000
    
    // === VARIABLE COSTS (beds, baths) ===
    const perBedroom = 5500;
    const perBathroom = 2000;
    
    // Calculate variable cost before soil multiplier
    const variableCost = (bedrooms * perBedroom) + 
                         (bathrooms * perBathroom);
    
    // Soil multipliers
    // Traditional: More expensive in clay, cheaper in sand
    const tradMultiplier = { slow: 1.4, medium: 1.0, fast: 0.65 }[soil];
    
    // Eljen: Better in clay (higher multiplier), cheaper in sand (lower multiplier)
    const eljenMultiplier = { slow: 1.0, medium: 1.0, fast: 0.8 }[soil];
    
    // Final prices = Baseline + (Variable × Soil Multiplier)
    // Minimum $16,000 for both systems
    const tradPrice = Math.max(16000, Math.round((tradBaseline + (variableCost * tradMultiplier)) / 100) * 100);
    const eljenPrice = Math.max(16000, Math.round((eljenBaseline + (variableCost * eljenMultiplier)) / 100) * 100);

    setCalculations({
      costTrad: tradPrice,
      costEljen: eljenPrice
    });
  }, [bedrooms, bathrooms, soil]);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prepare email data
    const emailData = {
      ...contactForm,
      sqft,
      bedrooms,
      bathrooms,
      soil,
      tradQuote: calculations.costTrad,
      eljenQuote: calculations.costEljen
    };

    try {
      // TODO: Replace with your actual email endpoint
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/septic-quote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(emailData)
      });

      if (response.ok) {
        toast({
          title: "Thank you for your submission!",
          description: "One of our estimators will reach out to you shortly.",
        });
        
        // Reset form
        setContactForm({
          name: '',
          phone: '',
          email: '',
          address: '',
          city: ''
        });
      } else {
        throw new Error('Submission failed');
      }
    } catch (error) {
      toast({
        title: "Submission Error",
        description: "Please call us at 1-888-907-3777 or email hello@groundhogs.ca",
        variant: "destructive"
      });
    }
  };

  const handleContactChange = (e) => {
    setContactForm({
      ...contactForm,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="gh-calculator-page" data-testid="calculator-page">
      <SEO
        title="Septic Cost Calculator | Groundhogs™ (Thunder Bay)"
        description="Estimate septic installation or replacement costs using our Thunder Bay-focused septic calculator."
        canonicalPath="/septic-calculator"
      />
      <Header />

      <div className="min-h-screen pt-24">
        <section className="py-12 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="gh-calculator-shell max-w-2xl mx-auto">
              {/* Back Button */}
              <div className="mb-6">
                <Link 
                  to="/" 
                  className="text-orange-600 hover:text-orange-700 font-medium flex items-center gap-2"
                >
                  <ArrowLeft size={20} />
                  Back to Home Page
                </Link>
              </div>

              {/* Calculator Card */}
              <div className="gh-calculator-card rounded-xl border bg-card text-card-foreground shadow-xl">
                {/* Header */}
                <div className="gh-calculator-header flex flex-col space-y-1.5 p-6 text-center border-b text-white rounded-t-lg">
                  <h1 className="font-semibold tracking-tight text-3xl md:text-4xl mb-2">
                    Instant Septic Installation Estimate
                  </h1>
                  <p className="text-gray-300">
                    Get your personalized dual-system quote in seconds
                  </p>
                </div>

                {/* Service Area Banner */}
                <div className="gh-calculator-banner py-3 px-6 text-center">
                  <p className="text-sm md:text-base font-semibold">
                    🏆 Proudly Serving Thunder Bay, Kenora, Dryden & Northwestern Ontario
                  </p>
                </div>

                {/* Calculator Body */}
                <div className="p-8">
                  {/* Square Footage Selection */}
                  <div className="mb-8">
                    <label htmlFor="square-footage" className="block text-sm font-semibold mb-2 text-gray-700">
                      Square Footage (approx)
                    </label>
                    <select
                      id="square-footage"
                      data-testid="calculator-square-footage"
                      value={sqft}
                      onChange={(e) => setSqft(e.target.value)}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-orange-500 text-gray-900 font-medium"
                    >
                      <option value="1000 or less">1000 or less</option>
                      <option value="1000-1500">1000-1500</option>
                      <option value="1500-2000">1500-2000</option>
                      <option value="2000+">2000+</option>
                    </select>
                  </div>

                  {/* Question 2: Bedrooms & Bathrooms */}
                  <div className="mb-8 grid grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="bedrooms" className="block text-sm font-semibold mb-2 text-gray-700">
                        Bedrooms
                      </label>
                      <select
                        id="bedrooms"
                        data-testid="calculator-bedrooms"
                        value={bedrooms}
                        onChange={(e) => setBedrooms(Number(e.target.value))}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg font-semibold text-lg focus:outline-none focus:border-orange-500"
                      >
                        {[1, 2, 3, 4, 5, 6, 7, 8].map(num => (
                          <option key={num} value={num}>{num}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label htmlFor="bathrooms" className="block text-sm font-semibold mb-2 text-gray-700">
                        Bathrooms
                      </label>
                      <select
                        id="bathrooms"
                        data-testid="calculator-bathrooms"
                        value={bathrooms}
                        onChange={(e) => setBathrooms(Number(e.target.value))}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg font-semibold text-lg focus:outline-none focus:border-orange-500"
                      >
                        {[1, 2, 3, 4, 5, 6].map(num => (
                          <option key={num} value={num}>{num}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Question 3: Soil Type */}
                  <div className="mb-8">
                    <label className="block text-sm font-semibold mb-3 text-gray-700">
                      Soil around your home
                    </label>
                    <div className="gh-soil-options grid grid-cols-3 gap-3" role="group" aria-label="Soil around your home">
                      {[
                        { value: 'slow', label: '🌧️ Clay (Slow)', desc: 'Clay/Heavy' },
                        { value: 'medium', label: '💧 Medium Loam', desc: 'Loam' },
                        { value: 'fast', label: '⚡ Fast (Sandy)', desc: 'Sandy' }
                      ].map(option => (
                        <button
                          key={option.value}
                          data-testid={`calculator-soil-${option.value}`}
                          aria-pressed={soil === option.value}
                          type="button"
                          onClick={() => setSoil(option.value)}
                          className={`p-4 border-2 rounded-lg text-center transition-all ${
                            soil === option.value
                              ? 'border-orange-500 bg-orange-50'
                              : 'border-gray-300 hover:border-orange-300'
                          }`}
                        >
                          <div className="text-2xl mb-1">{option.label.split(' ')[0]}</div>
                          <div className="font-semibold text-sm">{option.label.substring(option.label.indexOf(' ') + 1)}</div>
                          <div className="text-xs text-gray-600">{option.desc}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* DUAL QUOTE RESULTS */}
                  <div className="gh-quote-results border-2 border-orange-300 rounded-xl p-6 mb-6" data-testid="calculator-results" data-no-motion>
                    <h2 className="text-2xl font-bold mb-6 text-gray-900 text-center">
                      Your Dual System Quotes
                    </h2>
                    
                    <div className="grid md:grid-cols-2 gap-4 mb-4">
                      {/* Traditional System */}
                      <div className="bg-white p-8 rounded-lg shadow-md border-2 border-gray-200">
                        <h3 className="text-2xl font-bold mb-6 text-gray-900 text-center">
                          🛠️ Traditional System
                        </h3>
                        <div className="text-4xl font-bold text-orange-600 text-center" data-testid="calculator-total-traditional" aria-live="polite">
                          ${calculations.costTrad.toLocaleString()}
                        </div>
                      </div>

                      {/* Eljen System */}
                      <div className="bg-white p-8 rounded-lg shadow-md border-2 border-gray-200">
                        <h3 className="text-2xl font-bold mb-6 text-gray-900 text-center">
                          🚀 Eljen GSF (Advanced)
                        </h3>
                        <div className="text-4xl font-bold text-orange-600 text-center" data-testid="calculator-total-eljen" aria-live="polite">
                          ${calculations.costEljen.toLocaleString()}
                        </div>
                      </div>
                    </div>

                    <p className="text-xs text-center text-gray-600 italic mt-4">
                      *Pricing is based on averages and is subject to change. On-site evaluations are mandatory to properly determine pricing.
                    </p>
                  </div>

                  {/* Contact Form - Submit for Formal Quote */}
                  <form id="quote-form" data-testid="calculator-quote-form" onSubmit={handleSubmit} className="border-t-2 border-gray-200 pt-6 space-y-4">
                    <div className="mb-4">
                      <h3 className="text-xl font-bold text-gray-900 mb-2">
                        📋 Submit for Formal Quote
                      </h3>
                      <p className="text-sm text-gray-600">
                        Enter your contact information below and we&apos;ll provide a detailed formal quote within 24 hours.
                      </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="name" className="block text-sm font-medium mb-2 text-gray-700">
                          Full Name *
                        </label>
                        <input
                          id="name"
                          name="name"
                          type="text"
                          required
                          value={contactForm.name}
                          onChange={handleContactChange}
                          placeholder="John Smith"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-orange-500"
                        />
                      </div>
                      <div>
                        <label htmlFor="phone" className="block text-sm font-medium mb-2 text-gray-700">
                          Phone Number *
                        </label>
                        <input
                          id="phone"
                          name="phone"
                          type="tel"
                          required
                          value={contactForm.phone}
                          onChange={handleContactChange}
                          placeholder="(555) 123-4567"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-orange-500"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-medium mb-2 text-gray-700">
                        Email Address *
                      </label>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={contactForm.email}
                        onChange={handleContactChange}
                        placeholder="john@example.com"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-orange-500"
                      />
                    </div>

                    <div>
                      <label htmlFor="address" className="block text-sm font-medium mb-2 text-gray-700">
                        Property Address *
                      </label>
                      <input
                        id="address"
                        name="address"
                        type="text"
                        required
                        value={contactForm.address}
                        onChange={handleContactChange}
                        placeholder="123 Main Street, Thunder Bay, ON"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-orange-500"
                      />
                    </div>

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <p className="text-sm text-blue-800">
                        <strong>Your Estimates:</strong> Traditional: ${calculations.costTrad.toLocaleString()} | Eljen: ${calculations.costEljen.toLocaleString()}
                      </p>
                      <p className="text-xs text-blue-600 mt-1">
                        These estimates will be automatically included in your quote request.
                      </p>
                    </div>

                    <button
                      data-testid="calculator-submit-button"
                      type="submit"
                      className="w-full px-6 py-3 bg-orange-600 hover:bg-orange-700 text-white rounded-lg font-bold transition-colors text-lg"
                    >
                      Submit Quote Request
                    </button>
                  </form>

                  {/* Why Choose Section */}
                  <div className="border-t-2 border-gray-200 pt-6 mb-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">
                      Why Choose Groundhogs™ Septics?
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-4 rounded-lg border border-orange-200">
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 bg-orange-600 rounded-full flex items-center justify-center flex-shrink-0">
                            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
                            </svg>
                          </div>
                          <div>
                            <h4 className="font-bold text-gray-900 mb-1">3-Day Average Installation</h4>
                            <p className="text-sm text-gray-700">Systemized process completes most installs in about 3 days on average, not 5-7 days industry standard.</p>
                          </div>
                        </div>
                      </div>
                      <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-4 rounded-lg border border-orange-200">
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 bg-orange-600 rounded-full flex items-center justify-center flex-shrink-0">
                            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path>
                            </svg>
                          </div>
                          <div>
                            <h4 className="font-bold text-gray-900 mb-1">100% Permit Approval</h4>
                            <p className="text-sm text-gray-700">Expedited processing & engineering expertise. We handle all permits and inspections.</p>
                          </div>
                        </div>
                      </div>
                      <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-4 rounded-lg border border-orange-200">
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 bg-orange-600 rounded-full flex items-center justify-center flex-shrink-0">
                            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                            </svg>
                          </div>
                          <div>
                            <h4 className="font-bold text-gray-900 mb-1">Transparent Pricing</h4>
                            <p className="text-sm text-gray-700">Data-driven quotes from 1,200+ real installations. No hidden fees or surprises.</p>
                          </div>
                        </div>
                      </div>
                      <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-4 rounded-lg border border-orange-200">
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 bg-orange-600 rounded-full flex items-center justify-center flex-shrink-0">
                            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"></path>
                            </svg>
                          </div>
                          <div>
                            <h4 className="font-bold text-gray-900 mb-1">99% Client Satisfaction</h4>
                            <p className="text-sm text-gray-700">Proven track record with 1,200+ successful installations across Northwestern Ontario.</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="bg-slate-900 text-white p-4 rounded-lg text-center">
                      <p className="text-lg font-semibold mb-1">
                        🏆 1,200+ Installs • Thunder Bay & NW Ontario • 5-Year Warranty
                      </p>
                      <p className="text-sm text-gray-300">
                        Northwestern Ontario&apos;s most trusted septic installation experts
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
};

export default SepticCalculatorPage;
